<!-- @format -->
<template>
	<div class="center-header-container">
		<el-tooltip content="撤销 键入(Ctrl+Z)">
			<el-button size="small" @click="onUndo">撤销</el-button>
		</el-tooltip>
		<el-tooltip content="恢复 键入(Ctrl+Y)">
			<el-button size="small" @click="onRedo">恢复</el-button>
		</el-tooltip>

		<slot name="toolbar-save" :widget-datas="dragStore.widgetDatas" :form-attribute="dragStore.formAttribute">
			<el-button size="small" type="primary" :loading="saveLoading" @click="onSave">保存</el-button>
		</slot>
		<slot
			name="toolbar-preview"
			:widget-datas="dragStore.widgetDatas"
			:form-datas="getDefaultValue(dragStore.widgetDatas)"
		>
			<el-button size="small" :loading="previewStatus.loading" @click="onPreview">预览</el-button>
		</slot>
		<slot name="toolbar-right" />
	</div>

	<el-drawer
		:model-value="previewStatus.show"
		size="70%"
		:close-on-press-escape="false"
		title="预览"
		@close="previewStatus.show = false"
	>
		<div>
			<el-button type="primary" size="small" @click="onPreviewSave">获取数据</el-button>
			<el-button type="primary" size="small" @click="onPreviewReset">重置表单</el-button>
			<el-button type="primary" size="small" @click="previewStatus.disabled = true">禁用编辑</el-button>
			<el-button type="primary" size="small" @click="previewStatus.disabled = false">恢复编辑</el-button>
		</div>
		<el-divider style="margin: 10px 0" />
		<slot name="preview-top"></slot>
		<form-render
			v-if="previewStatus.show"
			ref="renderRef"
			v-model="previewStatus.formDatas"
			v-model:widget-datas="previewStatus.widgetDatas"
			:form-attribute="previewStatus.formAttribute"
			:disabled="previewStatus.disabled"
			:other-func="otherFunc"
			:name="name"
			:class="['preview-render', isMobile && 'mobile-preview-render']"
			form-status="preview"
			:is-mobile="isMobile"
		/>
		<slot name="preview-bottom"></slot>
	</el-drawer>

	<el-form v-show="false" ref="ruleFormRef" :model="fieldAttribute.data" :rules="fieldAttribute.rules">
		<el-form-item v-for="code in Object.keys(fieldAttribute.data)" :key="code" :prop="code" />
	</el-form>
</template>

<script setup lang="ts">
	import { useDragStoreWithOut } from '../../store';
	import { useHistoryStoreWithOut } from '../../store/history';
	import FormRender from '../../form-render/index.vue';
	import { getDefaultValue } from '../../utils/transWidgetDatas';
	import { deepClone, flat } from '../../utils/util';
	import { useBaseConfigStoreWithOut } from '../../config';
	import type { FieldsType, ScriptFuncType } from '../../types';
	import type { FormInstance, FormRules } from 'element-plus';

	interface Props {
		otherFunc?: ScriptFuncType;
		name?: string;
		isMobile?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emits = defineEmits(['on-save', 'on-save-error']);

	const dragStore = useDragStoreWithOut(props.name);
	const historyStore = useHistoryStoreWithOut(props.name);
	const baseConfigStore = useBaseConfigStoreWithOut(props.name);

	// 校验使用
	const ruleFormRef = ref<FormInstance>();

	// 渲染器ref
	const renderRef = ref<InstanceType<typeof FormRender>>();

	const saveLoading = ref(false);

	const previewStatus = ref<{
		loading: boolean;
		show: boolean;
		disabled: boolean;
		formDatas: Record<string, any>;
		widgetDatas: FieldsType[];
		formAttribute: Record<string, any>;
	}>({
		loading: false,
		show: false,
		disabled: false,
		formDatas: {},
		widgetDatas: [],
		formAttribute: {},
	});

	const fieldAttribute = ref<{
		data: Record<string, any>;
		rules: FormRules;
	}>({
		data: {},
		rules: {},
	});

	const onUndo = () => {
		historyStore.undo();
	};

	const onRedo = () => {
		historyStore.redo();
	};

	// 获取所有字段的rules
	const getFieldAttributeRule = (codes: string[], data: Record<string, any>): FormRules => {
		return codes.reduce((rules, code) => {
			const rule =
				typeof baseConfigStore.FIELDS_ATTRIBUTE[code]?.formItemProps === 'function'
					? baseConfigStore.FIELDS_ATTRIBUTE[code]?.formItemProps(data)?.rules || []
					: baseConfigStore.FIELDS_ATTRIBUTE[code]?.formItemProps?.rules || [];
			if (rule.length) {
				rules[code] = rule;
			}
			return rules;
		}, {});
	};

	// 对表单进行校验
	const validate = () =>
		new Promise((resolve) =>
			setTimeout(() => {
				ruleFormRef.value?.validate((isVaild) => {
					resolve(isVaild);
				});
			}),
		);

	// 校验每个字段的属性是否有报错
	const validateFieldAttribute = async () => {
		const flatArr = flat<FieldsType[]>(dragStore.widgetDatas);
		let errFields: FieldsType | null = null;
		let idx = -1;
		while (++idx < flatArr.length) {
			const data = flatArr[idx].pcProps;
			fieldAttribute.value = {
				data,
				rules: getFieldAttributeRule(Object.keys(data), data),
			};
			const res = await validate();
			if (res) {
				continue;
			} else {
				errFields = flatArr[idx];
				break;
			}
		}

		fieldAttribute.value = {
			data: {},
			rules: {},
		};

		if (errFields) {
			// 若拖入最后一个字段且有校验失败，直接设置selectField: errFields将不会触发right-aside/field组件的watch事件，所以先清空再赋值
			if (errFields.code === dragStore.selectField?.code) {
				dragStore.setState({ selectField: null });
				setTimeout(() => {
					dragStore.setState({ selectField: errFields });
				});
			} else {
				dragStore.setState({ selectField: errFields });
			}
		}
		return errFields;
	};

	const onSave = async () => {
		saveLoading.value = true;
		const errFields = await validateFieldAttribute();
		if (!errFields) {
			emits('on-save', { widgetDatas: dragStore.widgetDatas, formAttribute: dragStore.formAttribute }, () => {
				saveLoading.value = false;
			});
		} else {
			saveLoading.value = false;
			emits('on-save-error');
		}
	};

	const onPreview = async () => {
		previewStatus.value.loading = true;
		const errFields = await validateFieldAttribute();
		if (!errFields) {
			previewStatus.value = {
				loading: false,
				show: true,
				disabled: false,
				formDatas: deepClone(getDefaultValue(dragStore.widgetDatas)),
				widgetDatas: deepClone(dragStore.widgetDatas),
				formAttribute: deepClone(dragStore.formAttribute),
			};
		} else {
			previewStatus.value.loading = false;
		}
	};

	const onPreviewSave = () => {
		renderRef.value?.formRef?.validate((isVaild) => {
			if (isVaild) {
				console.log(previewStatus.value.formDatas);
			}
		});
	};

	const onPreviewReset = () => {
		renderRef.value?.formRef?.resetFields();
	};

	const keyupHandler = (event) => {
		if (event.ctrlKey && event.code === 'KeyZ') {
			onUndo();
		} else if (event.ctrlKey && event.code === 'KeyY') {
			onRedo();
		}
	};

	onMounted(() => {
		window.addEventListener('keyup', keyupHandler);
	});

	onUnmounted(() => {
		window.removeEventListener('keyup', keyupHandler);
	});

	// store唯一值
	provide('name', props.name);

	defineExpose({ validateFieldAttribute });
</script>

<style scoped lang="scss">
	.center-header-container {
		height: 100%;
		width: 100%;

		display: flex;
		align-items: center;
	}

	.preview-render {
		height: calc(100% - 48px) !important;
	}

	.mobile-preview-render {
		height: calc(100% - 100px);
		margin: 0 auto;
		width: 420px;
		border-radius: 15px;
		box-shadow: 0 0 1px 10px #495060;
		padding: 5px;
		overflow-y: scroll;
	}
</style>
